import React, { FormEvent, useEffect, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import { errorMessage } from '../services/manageApi';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

type AccessType = 'viewer' | 'competitor' | 'judge';

interface ManageCompetitionAddAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
}

const parseIdentifiers = (text: string): string[] => {
  const tokens = text
    .split(/\r?\n|,|;|\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
  return Array.from(new Set(tokens));
};

const ManageCompetitionAddAccessModal: React.FC<ManageCompetitionAddAccessModalProps> = ({
  isOpen,
  onClose,
  competitionId,
}) => {
  const [identifier, setIdentifier] = useState('');
  const [accessType, setAccessType] = useState<AccessType>('viewer');
  const [judgeMaxVotes, setJudgeMaxVotes] = useState<number>(10);
  const [entries, setEntries] = useState<string[]>([]);
  const [dropActive, setDropActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [results, setResults] = useState<{ created: number; updated: number; failed: number } | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const [failures, setFailures] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setIdentifier('');
    setEntries([]);
    setAccessType('viewer');
    setJudgeMaxVotes(10);
    setDropActive(false);
    setError('');
    setToastMessage('');
    setResults(null);
    setNotices([]);
    setFailures([]);
  }, [isOpen]);

  const entriesCount = entries.length;

  const addEntry = (value: string) => {
    const v = value.trim();
    if (!v) return;
    setEntries((prev) => (prev.includes(v) ? prev : [...prev, v]));
  };

  const removeEntry = (value: string) => {
    setEntries((prev) => prev.filter((e) => e !== value));
  };

  const handleFile = async (file: File) => {
    setError('');
    const content = await file.text();
    const ids = parseIdentifiers(content);
    if (ids.length === 0) {
      setError('No usernames/emails found in file');
      return;
    }
    setEntries((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);
    setNotices([]);
    setFailures([]);

    if (entries.length === 0) {
      setError('Add at least one username/email');
      return;
    }

    setIsLoading(true);
    try {
      let created = 0;
      let updated = 0;
      let failed = 0;
      const newNotices: string[] = [];
      const newFailures: string[] = [];
      const failedEntries: string[] = [];

      for (const entry of entries) {
        try {
          const res = await apiService.post('/competitions/add_access', {
            competition_id: competitionId,
            username: entry,
            access_type: accessType,
          });

          // If we are granting judge access, also ensure the Judges table row exists.
          // Note: the backend expects a judge *username*.
          if (accessType === 'judge') {
            const judge = res.data?.username || entry;
            const judgeRes = await apiService.post('/evaluations/add_judge', {
              competition_id: competitionId,
              judge,
              max_votes: judgeMaxVotes,
            });
            // Only host admins may change an existing judge's allowance.
            if (judgeRes.data?.action === 'exists') {
              newNotices.push(
                `${judge} is already a judge; allowance unchanged (host admins can change it on the Evaluation tab)`,
              );
            }
          }

          const action = res.data?.action;
          if (action === 'created') created += 1;
          else updated += 1;
        } catch (err) {
          console.error('Failed to add access for', entry, err);
          failed += 1;
          failedEntries.push(entry);
          newFailures.push(`${entry}: ${errorMessage(err, 'Failed to add access')}`);
        }
      }

      setResults({ created, updated, failed });
      // Keep only the entries that failed, so they can be fixed and retried; the rest are done.
      setEntries(failedEntries);
      setIdentifier('');
      setNotices(newNotices);
      setFailures(newFailures);
      setToastMessage('Access updates complete');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to add access';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  useEscapeToClose(onClose, {
    enabled: isOpen && !isLoading,
    confirmIfDirty: () => identifier.trim() !== '' || entries.length > 0,
  });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div className="register-modal" role="dialog" aria-modal="true">
          <div className="register-modal__header">
            <h2 className="register-modal__title">Add Access</h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            <div className="register-modal__fields">
              <label className="input-wrapper">
                <span className="input__label">Access Type</span>
                <select
                  className="input input--default input--md"
                  value={accessType}
                  onChange={(e) => setAccessType(e.target.value as AccessType)}
                >
                  <option value="viewer">viewer</option>
                  <option value="competitor">competitor</option>
                  <option value="judge">judge</option>
                </select>
              </label>

              {accessType === 'judge' && (
                <Input
                  type="number"
                  name="judgeMaxVotes"
                  label="Judge evaluation allowance"
                  placeholder="10"
                  value={String(judgeMaxVotes)}
                  onChange={(e) => setJudgeMaxVotes(Math.max(0, Number(e.target.value || 0)))}
                  disabled={isLoading}
                />
              )}

              <div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      name="identifier"
                      label="Add a username or email"
                      placeholder="netid or user@domain.com"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      addEntry(identifier);
                      setIdentifier('');
                    }}
                    disabled={isLoading}
                  >
                    Add
                  </Button>
                </div>

                <div
                  className={`mcm-dropzone${dropActive ? ' mcm-dropzone--active' : ''}`}
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: '10px',
                    textAlign: 'center',
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropActive(true);
                  }}
                  onDragLeave={() => setDropActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDropActive(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleFile(f);
                  }}
                >
                  <strong>Drag & drop</strong> a .txt/.csv file of usernames/emails here, or{' '}
                  <label className="mcm-link-label">
                    browse
                    <input
                      type="file"
                      accept=".txt,.csv"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFile(f);
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>
                </div>

                <p className="mcm-text-secondary" style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>
                  Entries to add: <strong>{entriesCount}</strong>
                </p>

                {entriesCount > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {entries.map((e) => (
                      <span
                        key={e}
                        className="mcm-chip"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.35rem 0.6rem',
                          borderRadius: '999px',
                          fontSize: '0.9rem',
                        }}
                      >
                        {e}
                        <button
                          type="button"
                          onClick={() => removeEntry(e)}
                          className="mcm-chip__remove"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            lineHeight: 1,
                          }}
                          aria-label={`Remove ${e}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {results && (
                <div className="mcm-text-secondary">
                  Results: created {results.created}, updated {results.updated}, failed {results.failed}
                </div>
              )}
              {notices.length > 0 && (
                <ul role="status" className="mcm-text-secondary" style={{ margin: 0 }}>
                  {notices.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
              {failures.length > 0 && (
                <ul className="register-modal__error" role="alert" style={{ margin: 0 }}>
                  {failures.map((f) => <li key={f}>{f}</li>)}
                </ul>
              )}
            </div>

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Close
              </Button>
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Apply Access
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />}
    </>
  );
};

export default ManageCompetitionAddAccessModal;
